import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, Search as SearchIcon, FileCode, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Loader } from '@/components/common/Loader';
import { FilterBar } from '@/components/common/FilterBar';
import { DateRangePicker, presetRange } from '@/components/common/DateRangePicker';
import { JsonToggle } from '@/components/common/JsonToggle';
import { Truncated } from '@/components/common/Truncated';
import { BulkImportDialog } from './BulkImportDialog';
import { catalogApi } from '@/api/endpoints';
import { formatDate, bsonId } from '@/lib/utils';
import { toast } from '@/store/toast';

const ALL = '__ALL__';

const STATUS_OPTIONS = [
  { value: 'SUSPECT',        label: 'Suspeito',              badge: 'destructive' as const },
  { value: 'INVESTIGATING',  label: 'Investigando',          badge: 'warning' as const },
  { value: 'KNOWN_CHEATER',  label: 'Cheater confirmado',    badge: 'destructive' as const },
  { value: 'FALSE_POSITIVE', label: 'Falso positivo',        badge: 'info' as const },
  { value: 'CLEAN',          label: 'Verificado (limpo)',    badge: 'success' as const },
  { value: 'ALLOWLIST',      label: 'Permitido (allowlist)', badge: 'success' as const },
  { value: 'EXEMPT',         label: 'Isento',                badge: 'secondary' as const },
];

function statusBadge(computed: string) {
  const opt = STATUS_OPTIONS.find(o => o.value.toLowerCase() === computed.toLowerCase());
  if (!opt) return <Badge variant="destructive">Suspeito</Badge>;
  return <Badge variant={opt.badge}>{opt.label}</Badge>;
}

export default function CatalogPage() {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [searchDeb, setSearchDeb] = useState('');
  const [kind, setKind] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [minRisk, setMinRisk] = useState('');
  const [historic, setHistoric] = useState(false);
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setSearchDeb(search.trim()), 400); return () => clearTimeout(t); }, [search]);

  const reload = useCallback(() => {
    setLoading(true);
    catalogApi.list({
      kind: kind === ALL ? undefined : kind as any,
      status: statusFilter === ALL ? undefined : statusFilter.toLowerCase(),
      search: searchDeb || undefined,
      minRisk: minRisk ? Number(minRisk) : undefined,
      historic,
      from, to,
      page,
      pageSize: 100
    }).then(r => {
      setData(r.items);
      setTotal(r.totalApprox);
    }).catch((e: any) => {
      toast.error('Falha ao carregar catálogo', e?.response?.data?.reason ?? e.message);
    }).finally(() => setLoading(false));
  }, [kind, statusFilter, searchDeb, minRisk, historic, from, to, page]);

  useEffect(() => { reload(); }, [reload]);

  function clearAll() {
    setSearch(''); setKind(ALL); setStatusFilter(ALL); setMinRisk(''); setHistoric(false); setFrom(undefined); setTo(undefined); setPage(1);
  }

  const active = [
    searchDeb && { key: 'q', label: 'Busca', value: searchDeb, onClear: () => setSearch('') },
    kind !== ALL && { key: 'k', label: 'Tipo', value: kind.toUpperCase(), onClear: () => setKind(ALL) },
    statusFilter !== ALL && { key: 's', label: 'Status', value: statusFilter, onClear: () => setStatusFilter(ALL) },
    minRisk && { key: 'r', label: 'Risco', value: `≥ ${minRisk}`, onClear: () => setMinRisk('') },
    historic && { key: 'h', label: 'Histórico', value: '30 dias', onClear: () => setHistoric(false) },
    from && { key: 'f', label: 'De', value: formatDate(from, 'dd/MM HH:mm'), onClear: () => setFrom(undefined) },
    to && { key: 't', label: 'Até', value: formatDate(to, 'dd/MM HH:mm'), onClear: () => setTo(undefined) },
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader
        title="Catálogo de artefatos"
        subtitle={`${total} artefato(s) suspeitos detectados. Marque os legítimos como verificados/permitidos para reduzir ruído.`}
        actions={
          <div className="flex gap-2">
            <JsonToggle data={data} label="Ver JSON" />
            <BulkImportDialog onImported={reload} />
          </div>
        }
      />

      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-72" placeholder="Buscar por nome, caminho, MD5, SHA-256 ou razão…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <Select value={kind} onValueChange={v => { setKind(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os tipos</SelectItem>
            <SelectItem value="exe">Executáveis (.exe)</SelectItem>
            <SelectItem value="dll">Bibliotecas (.dll)</SelectItem>
            <SelectItem value="driver">Drivers (.sys/.drv)</SelectItem>
            <SelectItem value="asset">Assets do jogo (.rez/.pak/.dat…)</SelectItem>
            <SelectItem value="script">Scripts (.lua/.py/.bat…)</SelectItem>
            <SelectItem value="other">Outros</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-48 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os status</SelectItem>
            <SelectItem value="suspect">Suspeitos (sem marca)</SelectItem>
            {STATUS_OPTIONS.filter(o => o.value !== 'SUSPECT').map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="h-8 w-28" type="number" min={0} placeholder="Risco ≥" value={minRisk} onChange={e => { setMinRisk(e.target.value); setPage(1); }} />
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          <input type="checkbox" checked={historic} onChange={e => { setHistoric(e.target.checked); setPage(1); }} />
          Incluir histórico 30 dias
        </label>
        <DateRangePicker from={from} to={to} onChange={r => { setFrom(r.from); setTo(r.to); setPage(1); }} label="(última detecção)" />
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last24h'); setFrom(r.from); setTo(r.to); }}>24h</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last7d'); setFrom(r.from); setTo(r.to); }}>7d</Button>
        </div>
      </FilterBar>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Artefato</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Risco</TableHead>
              <TableHead>HWIDs</TableHead>
              <TableHead>Vistos</TableHead>
              <TableHead>Última detecção</TableHead>
              <TableHead>Razão</TableHead>
              <TableHead className="sr-only">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9}><Loader /></TableCell></TableRow>}
            {!loading && data.length === 0 && <TableRow><TableCell colSpan={9}><EmptyState title="Nenhum artefato no catálogo" description="Ajuste os filtros ou aguarde novas detecções." /></TableCell></TableRow>}
            {data.map((c, i) => {
              const dk = (c.displayKind ?? c.artifactKind ?? 'other').toLowerCase();
              const ext = (c.fileExtension ?? '').toLowerCase();
              const kindBadge = dk === 'exe' ? <Badge variant="warning" title="Executável">EXE</Badge>
                : dk === 'dll' ? <Badge variant="info" title="Biblioteca dinâmica">DLL</Badge>
                : dk === 'driver' ? <Badge variant="destructive" title="Driver de sistema">DRIVER</Badge>
                : dk === 'asset' ? <Badge variant="secondary" title="Asset do jogo">{ext ? '.' + ext.toUpperCase() : 'ASSET'}</Badge>
                : dk === 'script' ? <Badge variant="warning" title="Script">{ext ? '.' + ext.toUpperCase() : 'SCRIPT'}</Badge>
                : <Badge variant="outline">{(ext || dk).toUpperCase()}</Badge>;
              const hwidsCount = Array.isArray(c.hwids) ? c.hwids.length : 0;
              return (
                <TableRow key={c._id || i}>
                  <TableCell>{kindBadge}</TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <Truncated mono value={c.artifactName ?? c.modulePath ?? '(sem nome)'} maxChars={60} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      SHA: <Truncated mono value={c._id} maxChars={20} />
                      {c.lastFileMd5 && <> · MD5: <Truncated mono value={c.lastFileMd5} maxChars={12} /></>}
                    </div>
                  </TableCell>
                  <TableCell>{statusBadge(c.computedStatus ?? 'suspect')}</TableCell>
                  <TableCell><Badge variant={(c.maxRiskScore ?? 0) >= 50 ? 'destructive' : 'warning'}>{c.maxRiskScore ?? 0}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">{hwidsCount}</TableCell>
                  <TableCell className="text-xs tabular-nums">{c.seenCount ?? 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(c.lastSeenAtUtc)}</TableCell>
                  <TableCell className="text-xs"><Truncated value={c.lastReason ?? c.reason} maxChars={50} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Link to={`/drill?type=file_md5&value=${encodeURIComponent(c.lastFileMd5 ?? c._id)}`}>
                        <Button size="sm" variant="ghost" title="Detalhar"><SearchIcon className="h-3 w-3" /></Button>
                      </Link>
                      <MarkArtifactDialog catalog={c} onSaved={reload} />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* paginação simples */}
      <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
        <span>Página {page} · {data.length} de {total} no total</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Anterior</Button>
          <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={data.length < 100 || loading}>Próxima</Button>
        </div>
      </div>
    </div>
  );
}

function MarkArtifactDialog({ catalog, onSaved }: { catalog: any; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const existing = catalog.mark;
  const existingStatus = existing?.status ? String(existing.status).toUpperCase() : 'CLEAN';
  const [status, setStatus] = useState<string>(existingStatus);
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [severity, setSeverity] = useState<number>(existing?.severity ?? 0);
  const [key, setKey] = useState<'source_hash' | 'module_path' | 'file_md5'>('source_hash');
  const [loading, setLoading] = useState(false);

  // reset quando abrir
  useEffect(() => {
    if (open) {
      setStatus(existing?.status ? String(existing.status).toUpperCase() : 'CLEAN');
      setNotes(existing?.notes ?? '');
      setSeverity(existing?.severity ?? 0);
      setKey((catalog.markKey ?? 'source_hash') as any);
    }
  }, [open, existing, catalog.markKey]);

  async function save() {
    setLoading(true);
    try {
      await catalogApi.mark(catalog._id, { status, notes: notes || undefined, severity, key });
      toast.success('Catálogo atualizado', `Artefato marcado como ${STATUS_OPTIONS.find(o => o.value === status)?.label ?? status}.`);
      setOpen(false);
      onSaved();
    } catch (e: any) {
      const reason = e?.response?.data?.reason;
      const friendly = reason === 'catalog_entry_not_found' ? 'Artefato não encontrado no catálogo.'
        : reason === 'catalog_has_no_file_md5' ? 'Este artefato não tem MD5 registrado. Use SHA-256 ou caminho do módulo.'
        : reason === 'catalog_has_no_module_path' ? 'Este artefato não tem caminho registrado.'
        : reason ?? e.message ?? 'Erro inesperado.';
      toast.error('Falha ao marcar', friendly);
    } finally {
      setLoading(false);
    }
  }

  const isAllowed = status === 'ALLOWLIST' || status === 'CLEAN';
  const isCheater = status === 'KNOWN_CHEATER';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={existing ? 'outline' : 'default'} title={existing ? 'Reclassificar' : 'Classificar artefato'}>
          {existing ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Classificar artefato no catálogo</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-muted/40 p-2 rounded border-l-2 border-info">
            <div className="flex items-center gap-2">
              <FileCode className="h-3 w-3 text-muted-foreground" />
              <Truncated mono value={catalog.artifactName ?? catalog.modulePath ?? '(sem nome)'} maxChars={70} />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 break-all">
              SHA: <span className="font-mono">{catalog._id}</span>
            </div>
          </div>

          <div>
            <Label>Status <span className="text-destructive">*</span></Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAllowed && (
              <div className="text-xs text-success mt-1 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Este artefato passará a ser tratado como legítimo.
              </div>
            )}
            {isCheater && (
              <div className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Detecções associadas serão priorizadas em alertas.
              </div>
            )}
          </div>

          <div>
            <Label>Chave da marca</Label>
            <Select value={key} onValueChange={v => setKey(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="source_hash">SHA-256 do binário (recomendado)</SelectItem>
                {catalog.lastFileMd5 && <SelectItem value="file_md5">MD5 do arquivo</SelectItem>}
                {(catalog.normalizedModulePath || catalog.modulePath) && <SelectItem value="module_path">Caminho do módulo</SelectItem>}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">
              SHA-256 é estável a renomeações. Caminho funciona para arquivos em local fixo.
            </div>
          </div>

          <div>
            <Label>Severidade (0 a 10)</Label>
            <Input type="number" min={0} max={10} value={severity} onChange={e => setSeverity(Number(e.target.value))} />
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Justifique a classificação para o histórico de auditoria…"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>Cancelar</Button>
          <Button onClick={save} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            {existing ? 'Atualizar marca' : 'Salvar classificação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
